import React from 'react';

import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useCreateMembership } from 'src/features/membership/api/useCreateMembership';
import { LIST_MEMBERSHIPS_RESOURCE } from 'src/features/membership/api/useListMemberships';
import CustomMembershipFormModal from 'src/features/membership/components/forms/custom/CustomMembershipFormModal';
import MembershipFormModal from 'src/features/membership/components/forms/MembershipFormModal';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface MembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateMembershipFormModal<TData>({ className, semantics }: MembershipFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { category = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useCreateMembership({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIPS_RESOURCE] });
            void navigate(`/${PATH.MEMBERSHIPS}/${category}/${success.id}`);

            toast({
                title: formatMessage(membershipMessages.createMembershipSuccess),
                description: formatMessage(membershipMessages.createMembershipSuccessDescription, {
                    entry: success.name,
                }),
            });
        },
    });

    return category === MEMBERSHIP_TEMPLATE_CATEGORY.CUSTOM ? (
        <CustomMembershipFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ parkingId, ...values }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(membershipMessages.addMembershipSubtitle)}
        />
    ) : (
        <MembershipFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ parkingId, ...values }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(membershipMessages.addMembershipSubtitle)}
        />
    );
}

export default CreateMembershipFormModal;
